import { useMemo } from "react";
import Icon from "../Icon";
import { Avatar, Badge, Barra } from "../Atoms";
import DonutChart from "../widgets/DonutChart";
import { PJ_ESTADOS, ST_CLR, ST_ICON, OBJETIVO_DIA } from "../../constants";
import { fmtHours, ringClr } from "../../utils";
import {
  avancePasos, avanceEsfuerzo, desvioEsfuerzo, desvioPlazo, embudo,
  cargaPersona, bandaOcupacion, salud, horasPorSemana, pasosDeTrabajo,
} from "../../kpisProyecto";
import { rutaCritica } from "../flow/flowGeometry";
import { clicable } from "../ClicableTeclado.jsx";

// Dashboard del proyecto. Aqui no hay aritmetica: toda vive en
// src/kpisProyecto.js, que es puro y se puede probar con node.
//
// Los graficos son divs y SVG a mano, como el resto del proyecto: barras con
// width en porcentaje (el patron de HomeDashboard) y el DonutChart que ya existe.

const Tarjeta = ({ ic, color, valor, label, sub, i }) => (
  <div className={"fu s" + Math.min(6, i + 1)} style={{ background: "var(--surface)",
    border: "1px solid var(--border)", borderRadius: 11, padding: "12px 13px",
    borderTop: "3px solid " + color }}>
    <Icon name={ic} size={15} color={color} style={{ marginBottom: 5, display: "block" }} />
    <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: ".08em", color: "var(--text3)", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 21, fontWeight: 800, color, lineHeight: 1 }}>{valor}</div>
    <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 2 }}>{sub}</div>
  </div>
);

const Panel = ({ titulo, sub, children, aviso }) => (
  <div style={{ background: "var(--surface)", border: "1px solid var(--border)",
    borderRadius: 12, padding: 14, minWidth: 0 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginBottom: 2 }}>
      <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: ".08em", color: "var(--text3)" }}>{titulo}</span>
      {aviso && (
        <span style={{ fontSize: 9.5, color: "var(--warn-text)", background: "var(--warn-bg)",
          border: "1px solid var(--warn-line)", borderRadius: 20, padding: "1px 7px" }}>{aviso}</span>
      )}
    </div>
    {sub && <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10,
      lineHeight: 1.5 }}>{sub}</div>}
    {!sub && <div style={{ height: 8 }} />}
    {children}
  </div>
);

const SinDato = ({ texto }) => (
  <div style={{ fontSize: 11.5, color: "var(--text3)", lineHeight: 1.6,
    padding: "10px 0" }}>{texto}</div>
);

export default function KpisView({
  proyecto, tareas = [], dependencias = [], users = [], festivos = {},
  minutosPorTarea = {}, minutosPorPersona = {}, porDia = [],
  tiempoParcial, tiempoError, onAbrirPaso,
}) {
  const trabajo = pasosDeTrabajo(tareas);

  const avP = useMemo(() => avancePasos(tareas), [tareas]);
  const avE = useMemo(() => avanceEsfuerzo(tareas, minutosPorTarea), [tareas, minutosPorTarea]);
  const de  = useMemo(() => desvioEsfuerzo(tareas, minutosPorTarea), [tareas, minutosPorTarea]);
  const dp  = useMemo(() => desvioPlazo(tareas, festivos), [tareas, festivos]);
  const emb = useMemo(() => embudo(tareas), [tareas]);
  const sal = useMemo(() => salud(proyecto, tareas, minutosPorTarea, festivos),
    [proyecto, tareas, minutosPorTarea, festivos]);
  const semanas = useMemo(() => horasPorSemana(porDia), [porDia]);

  const carga = useMemo(() => cargaPersona(
    tareas, minutosPorPersona, users,
    proyecto?.start_date && proyecto?.end_date
      ? { desde: proyecto.start_date, hasta: proyecto.end_date } : null,
    festivos, OBJETIVO_DIA
  ), [tareas, minutosPorPersona, users, proyecto, festivos]);

  // Se calcula sobre el grafo que hay, igual que en el lienzo.
  const rc = useMemo(() => {
    const nodos = {}; for (const t of tareas) nodos[String(t.id)] = t;
    const aristas = {}; for (const d of dependencias) aristas[String(d.id)] = d;
    return rutaCritica(nodos, aristas, OBJETIVO_DIA);
  }, [tareas, dependencias]);

  const completadas = trabajo.filter(t => t.status === "Completada").length;
  const bloqueadas  = trabajo.filter(t => t.status === "Bloqueada").length;

  const TARJETAS = [
    ["diagram-3", "#6366f1", trabajo.length, "Pasos",
      `${tareas.length - trabajo.length} de control`],
    ["check2-square", avP === null ? "#6b7280" : ringClr(avP),
      avP === null ? "—" : avP + "%", "Avance",
      avP === null ? "sin pasos" : `${completadas} de ${trabajo.length}`],
    ["clock", "#8b5cf6", de.real ? fmtHours(de.real / 60) : "—", "Horas reales",
      de.est ? `de ${fmtHours(de.est / 60)} estimadas` : "nadie ha estimado"],
    ["hourglass-split", de.pct === null ? "#6b7280" : (de.pct > 0 ? "#ef4444" : "#22c55e"),
      de.pct === null ? "—" : (de.pct > 0 ? "+" : "") + de.pct + "%", "Desvio de esfuerzo",
      de.sinEstimar ? `${de.sinEstimar} sin estimar` : "sobre lo estimado"],
    ["calendar-x", dp.max === null ? "#6b7280" : (dp.max > 0 ? "#ef4444" : "#22c55e"),
      dp.max === null ? "—" : (dp.max > 0 ? "+" : "") + dp.max + "d", "Desvio de plazo",
      dp.max === null ? "nada vencido aun" : `${dp.cuantosTarde} paso(s) tarde`],
    ["sign-stop-fill", bloqueadas ? "#ef4444" : "#6b7280", bloqueadas, "Bloqueados",
      emb.atascados.length ? `${emb.atascados.length} atascado(s)` : "nada atascado"],
  ];

  const topDesvio = de.porPaso.filter(p => p.pct !== null).slice(0, 8);
  const maxPlazo = Math.max(1, ...dp.porPaso.map(p => Math.abs(p.dias)));
  const topPlazo = dp.porPaso.filter(p => p.dias !== 0).slice(0, 8);
  const techoSemana = Math.max(1, ...semanas.map(s => s.minutes));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {tiempoError && (
        <div style={{ background: "var(--warn-bg)", border: "1px solid var(--warn-line)", color: "var(--warn-text)",
          borderRadius: 10, padding: "9px 12px", fontSize: 11.5, lineHeight: 1.55,
          display: "flex", gap: 7 }}>
          <Icon name="exclamation-triangle-fill" size={12} color="#a16207" />
          <span>
            No se ha podido leer el tiempo real del cuaderno. Todo lo que depende de las horas
            —el desvio de esfuerzo, el reparto por persona y el avance por esfuerzo— aparece
            como «—». <b>No significa cero horas: significa que no hay dato.</b>
          </span>
        </div>
      )}
      {tiempoParcial && (
        <div style={{ background: "var(--surface2)", border: "1px solid var(--border)",
          borderRadius: 10, padding: "8px 12px", fontSize: 11, color: "var(--text2)" }}>
          El reparto por persona solo incluye tus horas: el del resto del equipo es de
          administracion, igual que en Asistencias.
        </div>
      )}

      <div className="kpi-grid" style={{ display: "grid", gap: 10,
        gridTemplateColumns: "repeat(auto-fill,minmax(148px,1fr))" }}>
        {TARJETAS.map(([ic, c, v, l, s], i) => (
          <Tarjeta key={l} ic={ic} color={c} valor={v} label={l} sub={s} i={i} />
        ))}
      </div>

      <div className="grid-1col-mob" style={{ display: "grid", gap: 12,
        gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}>

        {/* Los dos avances. La DIFERENCIA entre ellos es el titular. */}
        <Panel titulo="Avance: pasos frente a esfuerzo"
          sub="Los dos miden lo mismo de forma distinta, y separarse es la señal.">
          <div style={{ display: "flex", gap: 18, alignItems: "center",
            justifyContent: "center", flexWrap: "wrap" }}>
            {[["Pasos", avP], ["Esfuerzo", avE]].map(([lab, v]) => (
              <div key={lab} style={{ textAlign: "center" }}>
                <div style={{ position: "relative", width: 96, height: 96 }}>
                  <DonutChart size={96} stroke={13} data={v === null
                    ? [{ value: 0, color: "var(--bg)" }]
                    : [{ value: v, color: ringClr(v) },
                       { value: 100 - v, color: "var(--bg)" }]} />
                  <div style={{ position: "absolute", inset: 0, display: "flex",
                    alignItems: "center", justifyContent: "center", fontSize: 19,
                    fontWeight: 800, color: v === null ? "var(--text3)" : ringClr(v) }}>
                    {v === null ? "—" : v + "%"}
                  </div>
                </div>
                <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 4,
                  fontWeight: 600 }}>{lab}</div>
              </div>
            ))}
          </div>
          {avP !== null && avE !== null && Math.abs(avP - avE) >= 15 && (
            <div style={{ marginTop: 10, fontSize: 11.5, lineHeight: 1.55,
              color: "var(--text2)", background: "var(--surface2)", padding: "8px 10px",
              borderRadius: 8 }}>
              {avE > avP
                ? <>Se ha gastado <b>mas presupuesto</b> del que corresponde a los pasos
                    cerrados: hay trabajo hecho que no se ha dado por terminado, o algun
                    paso se esta comiendo sus horas.</>
                : <>Hay <b>mas pasos cerrados</b> de lo que dice el esfuerzo gastado: o se
                    sobreestimo, o quedan los pasos mas caros por hacer.</>}
            </div>
          )}
          {avE === null && (
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--text3)" }}>
              El avance por esfuerzo necesita estimaciones. Sin ellas solo se puede contar
              pasos, que trata igual uno de una hora y uno de dos semanas.
            </div>
          )}
        </Panel>

        {/* Embudo */}
        <Panel titulo="Embudo por estado"
          sub={emb.cancelados ? `${emb.cancelados} cancelado(s), fuera del recuento` : null}>
          {!emb.total && <SinDato texto="Todavia no hay pasos de trabajo." />}
          {!!emb.total && (
            <div className="kpi-embudo">
              {PJ_ESTADOS.map(e => {
                const n = emb.conteo[e] || 0;
                const pct = Math.round(n / emb.total * 100);
                return (
                  <div key={e} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Icon name={ST_ICON[e] || "inbox"} size={12} color={ST_CLR[e]} />
                    <span style={{ fontSize: 11, color: "var(--text2)", width: 82,
                      flexShrink: 0 }}>{e}</span>
                    <div className="kpi-track" style={{ flex: 1 }}>
                      <div className="kpi-fill pbar" style={{ width: pct + "%",
                        background: ST_CLR[e], "--pct": pct + "%" }} />
                    </div>
                    <span style={{ fontSize: 11, fontFamily: "var(--mono)",
                      color: "var(--text3)", width: 34, textAlign: "right" }}>{n}</span>
                  </div>
                );
              })}
            </div>
          )}
          {!!emb.atascados.length && (
            <div style={{ marginTop: 11, paddingTop: 10, borderTop: "1px solid var(--border2)" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".07em", color: "#ef4444", marginBottom: 6 }}>
                Atascados mas de 5 dias
              </div>
              {emb.atascados.slice(0, 4).map(a => (
                <div key={a.id} className="btn" {...clicable(() => onAbrirPaso?.({ id: a.id }))}
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11,
                    padding: "4px 6px", borderRadius: 6, cursor: "pointer",
                    color: "var(--text2)" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%",
                    background: ST_CLR[a.status], flexShrink: 0 }} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap" }}>{a.title}</span>
                  {a.nota && (
                    <span title={a.nota} style={{ fontSize: 9.5, color: "var(--text3)",
                      maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap" }}>{a.nota}</span>
                  )}
                  <span style={{ fontFamily: "var(--mono)", fontSize: 10,
                    color: "#ef4444" }}>{a.dias}d</span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        {/* Previsto vs real */}
        <Panel titulo="Previsto frente a real"
          sub="Los ocho pasos que mas se han desviado, en cualquier direccion."
          aviso={de.sinEstimar ? `${de.sinEstimar} sin estimar` : null}>
          {!topDesvio.length && (
            <SinDato texto="Hace falta estimar algun paso para poder comparar. Sin estimacion no hay nada con lo que contrastar las horas." />
          )}
          {topDesvio.map(p => (
            <div key={p.id} className="kpi-pair btn" {...clicable(() => onAbrirPaso?.(p))}
              style={{ marginBottom: 9, cursor: "pointer" }}>
              <div style={{ display: "flex", gap: 6, fontSize: 11, marginBottom: 2 }}>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", color: "var(--text2)" }}>{p.title}</span>
                <span style={{ fontFamily: "var(--mono)", fontSize: 10.5,
                  color: p.pct > 0 ? "var(--red-text)" : "var(--ok-text)", fontWeight: 700 }}>
                  {p.pct > 0 ? "+" : ""}{p.pct}%
                </span>
              </div>
              {/* Dos barras emparejadas: previsto en gris, real en el color del
                  estado. Divs con width en %, como HomeDashboard. */}
              <div className="kpi-track" style={{ height: 6 }}>
                <div className="kpi-fill" style={{ width: Math.min(100, p.est / Math.max(p.est, p.real) * 100) + "%",
                  background: "var(--text3)", opacity: .45 }} />
              </div>
              <div className="kpi-track" style={{ height: 6 }}>
                <div className="kpi-fill" style={{ width: Math.min(100, p.real / Math.max(p.est, p.real) * 100) + "%",
                  background: ST_CLR[p.status] || "#6b7280" }} />
              </div>
            </div>
          ))}
          {!!topDesvio.length && (
            <div style={{ fontSize: 9.5, color: "var(--text3)", marginTop: 4,
              display: "flex", gap: 10 }}>
              <span><span style={{ display: "inline-block", width: 10, height: 4,
                background: "var(--text3)", opacity: .45, marginRight: 3 }} />previsto</span>
              <span><span style={{ display: "inline-block", width: 10, height: 4,
                background: "var(--accent)", marginRight: 3 }} />real</span>
            </div>
          )}
        </Panel>

        {/* Desvio de plazo: barras divergentes sobre el eje cero */}
        <Panel titulo="Desvio de plazo"
          sub="Dias laborables de adelanto o retraso frente a la fecha prevista.">
          {!topPlazo.length && (
            <SinDato texto={dp.evaluados
              ? "Todo lo evaluado cerro el dia previsto."
              : "Nada ha vencido ni se ha cerrado todavia, asi que no hay desvio que medir."} />
          )}
          {topPlazo.map(p => {
            const tarde = p.dias > 0;
            const ancho = Math.abs(p.dias) / maxPlazo * 50;
            return (
              <div key={p.id} className="btn" {...clicable(() => onAbrirPaso?.(p))}
                style={{ marginBottom: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", gap: 6, fontSize: 11, marginBottom: 2 }}>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", color: "var(--text2)" }}>{p.title}</span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: 10.5, fontWeight: 700,
                    color: tarde ? "var(--red-text)" : "var(--ok-text)" }}>
                    {tarde ? "+" : ""}{p.dias}d
                  </span>
                </div>
                <div className="kpi-div">
                  <div style={{ position: "absolute", top: 2, bottom: 2, borderRadius: 3,
                    background: tarde ? "var(--red)" : "var(--green)",
                    left: tarde ? "50%" : `${50 - ancho}%`, width: ancho + "%" }} />
                </div>
              </div>
            );
          })}
        </Panel>

        {/* Carga por persona */}
        <Panel titulo="Reparto de carga"
          sub={carga.length
            ? "Solo cargan los encargados: un responsable a quien preguntar no esta ocupado por el paso."
            : null}
          aviso={tiempoParcial ? "solo tus horas" : null}>
          {!carga.length && <SinDato texto="Nadie tiene pasos asignados todavia." />}

          {/* Una FILA por persona, no una tabla de cinco columnas.
              La tabla estaba bien en un panel ancho, pero estos van en una
              rejilla de minimo 300 px: a ese ancho solo se veian «Persona» y
              «Pend.» y lo demas se iba por un scroll horizontal que nadie mira.
              Y lo que se iba era justo la ocupacion, que es la pregunta del
              panel: quien va sobrecargado.

              Apilado cabe en cualquier ancho, y la barra pasa a ser lo primero
              que se ve en vez de la ultima columna. */}
          {carga.map(f => {
            const banda = bandaOcupacion(f.ocupacion);
            const pct = f.ocupacion === null ? null : Math.round(f.ocupacion * 100);
            return (
              <div key={f.user_id} style={{ padding: "8px 0",
                borderTop: "1px solid var(--border2)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5 }}>
                  <Avatar name={f.name} size={22} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "var(--text)",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {f.name}
                  </span>
                  {!f.pasos && (
                    <span title="Tiene horas imputadas pero ningun paso asignado"
                      style={{ fontSize: 9.5, color: "var(--text3)" }}>sin pasos</span>
                  )}
                  {banda
                    ? <Badge label={`${pct}%`} color={banda.color} bg={banda.color + "18"} />
                    : <span style={{ fontSize: 10.5, color: "var(--text3)" }}>sin ocupacion</span>}
                </div>

                {/* La barra se acota al 100 pero el porcentaje de al lado no: a
                    quien va al 160% hay que verlo desbordado en la etiqueta, no
                    con una barra mas larga que su caja. */}
                {banda && <Barra valor={Math.min(100, pct)} color={banda.color} alto={6} />}

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4,
                  fontSize: 10.5, color: "var(--text3)" }}>
                  <span>{banda ? banda.label : "sin fechas de proyecto"}</span>
                  <span style={{ flex: 1 }} />
                  <span title="Horas estimadas de lo que le queda abierto">
                    pend. <b style={{ fontFamily: "var(--mono)", color: "var(--text2)" }}>
                      {f.estPend ? fmtHours(f.estPend / 60) : "—"}</b>
                  </span>
                  <span title="Horas que ya ha imputado a este proyecto">
                    real <b style={{ fontFamily: "var(--mono)", color: "var(--text2)" }}>
                      {f.real ? fmtHours(f.real / 60) : "—"}</b>
                  </span>
                  {f.saldo !== null && (
                    <span title="Lo que lleva hecho frente a lo que tenia estimado">
                      saldo <b style={{ fontFamily: "var(--mono)",
                        color: f.saldo > 0 ? "var(--red-text)" : "var(--ok-text)" }}>
                        {(f.saldo > 0 ? "+" : "") + fmtHours(f.saldo / 60)}</b>
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {!!carga.length && carga[0].capacidad === null && (
            <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 7,
              lineHeight: 1.5 }}>
              Sin fechas de inicio y fin del proyecto no se puede calcular la ocupacion:
              dividir por una capacidad inventada daria un porcentaje que parece medido.
            </div>
          )}
        </Panel>

        {/* Ruta critica */}
        <Panel titulo="Ruta critica"
          sub="El camino mas largo del flujo: es lo que marca la duracion del proyecto.">
          {rc.ciclo && (
            <div style={{ fontSize: 11.5, lineHeight: 1.6, color: "var(--warn-text)",
              background: "var(--warn-bg)", border: "1px solid var(--warn-line)", borderRadius: 8,
              padding: "9px 11px" }}>
              <b>No se puede calcular:</b> el flujo tiene {rc.aristasCiclo.length} conexion(es)
              que cierran un bucle. Un retrabajo es legitimo, pero mientras exista no hay un
              «camino mas largo» que medir. Se ven marcadas en ambar en la pestaña Flujo.
            </div>
          )}
          {!rc.ciclo && rc.ids.length <= 1 && (
            <SinDato texto="Hacen falta pasos conectados entre si. Conecta el flujo en la pestaña Flujo y aqui aparecera la cadena que marca el plazo." />
          )}
          {!rc.ciclo && rc.ids.length > 1 && (
            <>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                {rc.ids.map((id, i) => {
                  const t = tareas.find(x => String(x.id) === String(id));
                  if (!t) return null;
                  return (
                    <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {i > 0 && <Icon name="chevron-right" size={9} color="var(--text3)" />}
                      <span className="btn chip" {...clicable(() => onAbrirPaso?.(t))}
                        title={`${t.status}`}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 20, cursor: "pointer",
                          fontSize: 10.5, background: (ST_CLR[t.status] || "#6b7280") + "18",
                          color: ST_CLR[t.status] || "#6b7280", fontWeight: 600,
                          maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis",
                          whiteSpace: "nowrap" }}>
                        {i + 1}. {t.title}
                      </span>
                    </span>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 9 }}>
                {rc.ids.length} pasos · <b style={{ color: "var(--text2)" }}>{rc.dias} dias</b>
                {" "}de duracion encadenada
              </div>
            </>
          )}
        </Panel>

        {/* Salud */}
        <Panel titulo="Salud del proyecto"
          sub="Compuesto de tres ejes. Es una lectura opinable, asi que se muestran por separado."
          aviso={sal && !sal.completa ? `solo ${sal.ejes} de 3 ejes` : null}>
          {!sal && <SinDato texto="Hace falta al menos un paso con estimacion o con estado para poder decir algo." />}
          {sal && (
            <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative", width: 88, height: 88, flexShrink: 0 }}>
                <DonutChart size={88} stroke={12} data={[
                  { value: sal.valor, color: ringClr(sal.valor) },
                  { value: 100 - sal.valor, color: "var(--bg)" }]} />
                <div style={{ position: "absolute", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center", fontSize: 18,
                  fontWeight: 800, color: ringClr(sal.valor) }}>{sal.valor}</div>
              </div>
              <div style={{ flex: 1, minWidth: 160 }}>
                {sal.partes.map(p => (
                  <div key={p.key} style={{ marginBottom: 7 }}>
                    <div style={{ display: "flex", fontSize: 10.5, marginBottom: 2 }}>
                      <span style={{ flex: 1, color: "var(--text2)" }}>{p.label}</span>
                      <span style={{ fontFamily: "var(--mono)", color: "var(--text3)" }}>
                        {p.valor === null ? "sin datos" : Math.round(p.valor * 100) + "%"}
                      </span>
                    </div>
                    <div className="kpi-track" style={{ height: 5 }}>
                      {p.valor !== null && (
                        <div className="kpi-fill" style={{ width: p.valor * 100 + "%",
                          background: ringClr(p.valor * 100) }} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>

        {/* Horas por semana */}
        {!!semanas.length && (
          <Panel titulo="Horas por semana"
            sub="Del cuaderno, agrupadas por el lunes de cada semana.">
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 110,
              paddingTop: 6, justifyContent: "flex-start" }}>
              {semanas.slice(-16).map(s => {
                const h = s.minutes / techoSemana * 100;
                return (
                  // maxWidth ademas de flex: con una o dos semanas, flex:1 sola
                  // convierte la barra en un bloque de color del ancho del panel.
                  <div key={s.semana} style={{ flex: 1, minWidth: 8, maxWidth: 46, display: "flex",
                    flexDirection: "column", alignItems: "center", height: "100%" }}
                    title={`Semana del ${s.semana}: ${fmtHours(s.minutes / 60)}`}>
                    <div style={{ flex: 1, width: "100%", display: "flex",
                      alignItems: "flex-end" }}>
                      <div className="pbar" style={{ width: "100%",
                        height: Math.max(2, h) + "%", background: "#8b5cf6",
                        borderRadius: "3px 3px 0 0", "--pct": Math.max(2, h) + "%" }} />
                    </div>
                    <div style={{ fontSize: 8, color: "var(--text3)", marginTop: 3,
                      fontFamily: "var(--mono)" }}>{s.semana.slice(5).replace("-", "/")}</div>
                  </div>
                );
              })}
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
